import { useEffect, useRef } from "react";
import {
  FieldPath,
  FieldValue,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { Field, Input, Textarea } from "shared/components/Spor";
import { Language, languageToName } from "shared/types/language";
import { EndsWith } from "shared/utils/objectUtils";

type CustomInputProps<T extends FieldValues> = {
  language: Language;
  titlePlaceholder?: string;
  hasTitleAndDescription?: boolean;
  customFormField: FieldPath<T> &
    EndsWith<"texts" | "description" | "Description">; // Description is capitalized in StationPerson
};

export const CustomInput = <T extends FieldValues>({
  language,
  titlePlaceholder,
  hasTitleAndDescription,
  customFormField,
}: CustomInputProps<T>) => {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<T>();

  const descriptionField = hasTitleAndDescription ? ".description" : "";
  const pathTitle = `${customFormField}.${language}.title` as FieldPath<T>;
  const pathDescription =
    `${customFormField}.${language}${descriptionField}` as FieldPath<T>;
  const title = watch(pathTitle);
  const description = watch(pathDescription);

  const languageName = languageToName[language];

  /**
   * This useEffect is used to update the values of customFormTitleField
   * whenever titlePlaceholders changes and the title had the same value
   * as the previous titlePlaceholder.
   */
  const prevTitlePlaceholder = useRef<string | undefined>(titlePlaceholder);
  useEffect(() => {
    if (!hasTitleAndDescription || !titlePlaceholder) return;
    const currentTitlePlaceholder = titlePlaceholder;
    if (title === currentTitlePlaceholder) return;
    if (title !== prevTitlePlaceholder.current) return;
    setValue(pathTitle, currentTitlePlaceholder as FieldValue<T>);
    prevTitlePlaceholder.current = titlePlaceholder;
  }, [titlePlaceholder]);

  /**
   * Fields are nullish, therefore not enforced. In order to enforce the languages
   * shown in frontend (e.g NOB|ENG or SWE|ENG), we set them to "" at render to make
   * them have to fullfill the z.string().min(1) criteria.
   */
  useEffect(() => {
    const titleShouldReset = hasTitleAndDescription && !titlePlaceholder;
    const titleIsNullish = title === undefined || title === null;
    const descriptionIsNullish =
      description === undefined || description === null;
    if (titleShouldReset && titleIsNullish) {
      setValue(pathTitle, "" as FieldValue<T>);
    }
    if (descriptionIsNullish) {
      setValue(pathDescription, "" as FieldValue<T>);
    }
  }, [title, description]);

  return (
    <>
      {hasTitleAndDescription && (
        <Field>
          <Input
            label={t(`${languageName} tittel`)}
            // Must cast the values below since we can only assume that the customFormTitleField sent is correct
            {...register(pathTitle, {
              value: (titlePlaceholder ?? "") as FieldValue<T>,
            })}
          />
          <RenderErrorInPath errors={errors} errorPath={pathTitle} />
        </Field>
      )}
      <Field>
        <Textarea
          label={t(`${languageName} tekst`)}
          // Must cast the values below since we can only assume that the customFormDescriptionField sent is correct
          {...register(pathDescription, {
            value: "" as FieldValue<T>,
          })}
        />
        <RenderErrorInPath errors={errors} errorPath={pathDescription} />
      </Field>
    </>
  );
};
